/* MorningAcknowledgement.jsx — ambient operational greeting.
 *
 * A single, calm, time-aware line that quietly tells the operator
 * what's waiting today. Designed to feel like a status line on a
 * premium operating system — not a notification, not an alert,
 * never assistant-like.
 *
 * Doctrine (verbatim from user brief):
 *   • Time-aware greeting (Morning / Afternoon / Evening) — no "Good".
 *   • Hidden after 21:00 and before 05:00 to stay calm at night.
 *   • Surfaces needs-attention FIRST, follow-ups SECOND.
 *     - blockers  → "{n} item(s) need attention"
 *     - follow-ups → "{n} follow-up(s) waiting today"
 *     - both     → joined by " · "
 *   • Renders nothing when neither category has anything.
 *   • Single line, lightweight typography, soft hierarchy.
 *   • Fades to 0 as the page scrolls past ~80px so the screen
 *     calms down the moment the operator engages with work.
 *
 * No exclamation marks. No emojis. No "ChainFlow remembered…".
 * Just operational presence.
 */
import React, { useEffect, useState } from "react";

const timeGreeting = (hour) => {
  if (hour < 5)  return null;   // night — hide
  if (hour < 12) return "Morning";
  if (hour < 17) return "Afternoon";
  if (hour < 21) return "Evening";
  return null;                  // after 21:00 — hide
};

const formatParts = (needs, followUps) => {
  const parts = [];
  if (needs > 0) {
    parts.push(`${needs} item${needs === 1 ? "" : "s"} need${needs === 1 ? "s" : ""} attention`);
  }
  if (followUps > 0) {
    parts.push(`${followUps} follow-up${followUps === 1 ? "" : "s"} waiting today`);
  }
  return parts.join(" · ");
};

export function MorningAcknowledgement({
  firstName,
  needsAttention = 0,
  followUpsDue = 0,
  className = "",
}) {
  const [opacity, setOpacity] = useState(1);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY || window.pageYOffset || 0;
      // Linear fade from 0 → 80px, opacity 1 → 0.
      const next = Math.max(0, Math.min(1, 1 - y / 80));
      setOpacity(next);
    };
    onScroll(); // sync on mount in case page is restored mid-scroll
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const hour = new Date().getHours();
  const greeting = timeGreeting(hour);
  if (!greeting) return null;

  const needs = Number(needsAttention) || 0;
  const fups  = Number(followUpsDue) || 0;
  if (needs === 0 && fups === 0) return null;

  const body = formatParts(needs, fups);
  const name = (firstName || "").trim() || "there";

  return (
    <p
      className={`text-[12.5px] md:text-[13px] text-slate-500 leading-snug tracking-[-0.005em] transition-opacity duration-150 ease-out ${className}`}
      style={{
        opacity,
        // Once the line is functionally invisible, stop intercepting
        // clicks so it never blocks the deal table underneath.
        pointerEvents: opacity < 0.05 ? "none" : "auto",
        willChange: "opacity",
      }}
      data-testid="today-morning-acknowledgement"
    >
      <span className="text-slate-600 font-medium">{greeting} {name}.</span>
      <span className="text-slate-300 mx-1.5">·</span>
      <span>{body}.</span>
    </p>
  );
}

export default MorningAcknowledgement;
